import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  OnDestroy,
  Renderer2,
  signal,
  Signal,
  untracked,
  ViewEncapsulation,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';

import { SecurityService } from '@ups/security';
import { PermissionType, SecurityConstants } from '@ups/security-constants';
import {
  HelpBaseComponent,
  HelpDocumentDto,
  HelpState,
  HelpTypeEnum,
} from '@ups/xplat/base/in-app-help';

@Component({
  selector: 'help-control',
  templateUrl: './help.component.html',
  styles: [
    `
      help-control {
        position: absolute;
        right: 0em;
        top: 0;
      }
    `,
  ],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  // One control per help anchor, hundreds on a grid: never Default (Bug 35599).
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HelpComponent extends HelpBaseComponent implements OnDestroy {
  tooltip = signal<HelpDocumentDto[] | undefined>(undefined);
  private docs: Record<string, HelpDocumentDto[]> = {};

  // Reactive (permissionsChanged), so a live grant or revocation re-evaluates the gate
  // under OnPush (codex-ryn review, thread 92851).
  private readonly canReadTooltips: Signal<boolean>;

  // Memoized: the permission is read only once tooltips are on and this path has one,
  // not on every change-detection pass per anchor as the old getter did (Bug 35599).
  readonly shouldRender = computed(
    () =>
      this.showTooltips() && !!this.tooltip()?.length && this.canReadTooltips()
  );

  private parentPositioned = false;

  constructor(
    private renderer: Renderer2,
    private element: ElementRef,
    private store: Store,
    private security: SecurityService
  ) {
    super();
    this.canReadTooltips = security.getPermissionStatus(
      SecurityConstants.employee_portal_frameworkfeatures_tooltips,
      PermissionType.readAll
    );
    store.pipe(HelpState.selectShowTooltips(this.destroy$)).subscribe((x) => {
      this.showTooltips.set(x);
    });

    store
      .select(HelpState.selectDocumentDictionary)
      .pipe(takeUntil(this.destroy$))
      .subscribe((docs) => {
        this.docs = docs;
        this.refreshTooltip();
      });

    // Position the parent only when the lightbulb first renders: reading its computed style
    // forces a layout, and most anchors never show anything (Bug 35599).
    effect(() => {
      if (this.shouldRender()) untracked(() => this.positionParent());
    });
  }

  private refreshTooltip(): void {
    this.tooltip.set(
      (this.docs[this.walkthroughPathValue] || []).filter(
        (x) => x.type === HelpTypeEnum.Tooltip
      )
    );
  }

  /** The host anchor changed after init (Bug 36097). The directive owns the path registration. */
  moveTo(walkthroughId: string, walkthroughPath: string): void {
    this.walkthroughIdValue = walkthroughId;
    if (walkthroughPath === this.walkthroughPathValue) return;
    this.walkthroughPathValue = walkthroughPath;
    this.refreshTooltip();
  }

  onClick() {
    // this.container.onElementClick.emit({
    //   elementId: this.walkthroughIdValue,
    //   elementPath: this.walkthroughPathValue,
    //   type: type,
    //   edit: shouldEdit
    // } as WalkthroughElementClickEvent);
  }

  private positionParent(): void {
    if (this.parentPositioned) return;
    this.parentPositioned = true;
    // Fixing position (web only): this control is position:absolute, so a STATIC parent needs
    // relative to anchor it. A parent that is already positioned (the sticky ups-sub-bar filter
    // bars, for one) anchors it as is; overwriting that broke their sticky layout (Bug 36078).
    const parent = this.element?.nativeElement?.parentNode;
    if (
      parent instanceof Element &&
      getComputedStyle(parent).position === 'static'
    ) {
      this.renderer.setStyle(parent, 'position', 'relative');
    }
  }

  override ngOnDestroy(): void {
    // Completes destroy$; without it both store subscriptions outlived the control (Bug 35599).
    super.ngOnDestroy();
  }
}
